import { submitUserReport, type UserReportFields } from "@/lib/userReports";
import { UserReportCategory } from "@openapi";
import { App, Form, Input, Modal, Select } from "antd";
import type { Rule } from "antd/es/form";
import { useState } from "react";
import { useTranslation } from "react-i18next";

// The backend accepts these lengths (blocks-code/management#136).
const TITLE_MIN = 3;
const TITLE_MAX = 200;
const DESCRIPTION_MIN = 10;
const DESCRIPTION_MAX = 5000;

const CATEGORIES = [
    UserReportCategory.Bug,
    UserReportCategory.Content,
    UserReportCategory.Suggestion,
    UserReportCategory.Other,
];

const FIELDS = ["category", "title", "description"] as const;

interface ReportProblemModalProps {
    open: boolean;
    onClose: () => void;
}

// A logged-in user reports a bug, content problem or suggestion
// (blocks-code/management#118). The page URL and browser are attached
// automatically.
export default function ReportProblemModal({
    open,
    onClose,
}: ReportProblemModalProps) {
    const { t } = useTranslation();
    const { message } = App.useApp();
    const [form] = Form.useForm<UserReportFields>();
    const [busy, setBusy] = useState(false);

    // Surrounding spaces are trimmed before sending, so they do not count.
    const trimmedLength =
        (min: number, max: number, key: string): Rule =>
        () => ({
            validator: (_, value?: string) => {
                const length = (value ?? "").trim().length;
                return length >= min && length <= max
                    ? Promise.resolve()
                    : Promise.reject(new Error(t(key)));
            },
        });

    const submit = async (values: UserReportFields) => {
        if (busy) return;
        setBusy(true);
        try {
            const result = await submitUserReport(
                {
                    category: values.category,
                    title: values.title.trim(),
                    description: values.description.trim(),
                },
                {
                    href: window.location.href,
                    userAgent: navigator.userAgent,
                },
                t,
            );
            if (result.ok) {
                message.success(t("report.success"));
                onClose();
                return;
            }
            const fieldErrors = FIELDS.filter(
                (name) => result.fieldErrors[name],
            ).map((name) => ({ name, errors: [result.fieldErrors[name]] }));
            if (fieldErrors.length > 0) {
                form.setFields(fieldErrors);
            } else {
                message.error(result.message);
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            title={t("report.title")}
            open={open}
            onOk={() => form.submit()}
            onCancel={onClose}
            okText={t("report.submit")}
            cancelText={t("report.cancel")}
            confirmLoading={busy}
            // Reset once the close animation ends, so the fields do not
            // visibly empty while the modal fades out.
            afterClose={() => form.resetFields()}
            destroyOnHidden
        >
            <p>{t("report.intro")}</p>
            <Form
                form={form}
                name="report"
                layout="vertical"
                initialValues={{ category: UserReportCategory.Bug }}
                onFinish={submit}
                disabled={busy}
            >
                <Form.Item
                    name="category"
                    label={t("report.field.category")}
                    rules={[{ required: true }]}
                >
                    <Select
                        options={CATEGORIES.map((category) => ({
                            value: category,
                            label: t(`report.category.${category}`),
                        }))}
                    />
                </Form.Item>
                <Form.Item
                    name="title"
                    label={t("report.field.title")}
                    required
                    rules={[
                        trimmedLength(
                            TITLE_MIN,
                            TITLE_MAX,
                            "report.validation.titleLength",
                        ),
                    ]}
                >
                    <Input
                        maxLength={TITLE_MAX}
                        placeholder={t("report.placeholder.title")}
                    />
                </Form.Item>
                <Form.Item
                    name="description"
                    label={t("report.field.description")}
                    required
                    rules={[
                        trimmedLength(
                            DESCRIPTION_MIN,
                            DESCRIPTION_MAX,
                            "report.validation.descriptionLength",
                        ),
                    ]}
                    // The count sits below the box; keep it off the footer.
                    style={{ marginBottom: 32 }}
                >
                    <Input.TextArea
                        maxLength={DESCRIPTION_MAX}
                        showCount
                        rows={6}
                        placeholder={t("report.placeholder.description")}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
}
